// @ts-check
import React, { useEffect, useMemo, useRef, useState } from "react"
import { flexRender } from "@tanstack/react-table"
import { classNames } from "primereact/utils"
import { buildPinnedTableStyle } from "../utils/TableLayout.utils"
import { TableFilterButton } from "./filter/TableFilterButton.component"
import { TableFilterOverlay } from "./filter/TableFilterOverlay.component"

/**
 * @param {import("./TableHeadCell.component.types").TableHeadCellProps} props
 */
export const TableHeadCell = (props) => {
  const { header, openOverlayRef } = props

  /** @type {React.MutableRefObject<import("primereact/overlaypanel").OverlayPanel | null>} */
  const overlayRef = useRef(null)

  /** @type {import("../types/table").ExpandedColumnMeta | undefined} */
  const columnMeta = header.column.columnDef.meta

  const canSort = header.column.getCanSort()
  const sortDir = header.column.getIsSorted()
  const canFilter = header.column.getCanFilter()
  const isFiltered = header.column.getIsFiltered()
  const filterValue = header.column.getFilterValue()
  const table = header.getContext().table
  const tableRows = table.getRowModel().rows
  const hasData = useMemo(() => tableRows.length > 0, [tableRows])
  const hasActiveFilters = table.getState().columnFilters.length > 0
  const isSortEnabled = canSort && hasData

  const [filterDraftValue, setFilterDraftValue] = useState(
    filterValue ?? undefined
  )

  useEffect(() => {
    setFilterDraftValue(filterValue ?? undefined)
  }, [filterValue])

  const className = classNames(
    "table__cell table__cell--head",
    {
      "table__cell--sortable": isSortEnabled,
      "table__cell--filterable": canFilter,
      "table__cell--disabled-filter": !hasData && !hasActiveFilters,
      "table__cell--disabled-sort": !isSortEnabled,
    },
    columnMeta?.className
  )

  const pinnedStyle = useMemo(
    () => buildPinnedTableStyle(header.column),
    [header.column]
  )

  /**
   * @param {false | import("@tanstack/react-table").SortDirection} sortDir
   */
  const getSortIconClassName = (sortDir) => {
    switch (sortDir) {
      case "asc":
        return "pi-sort-amount-up"
      case "desc":
        return "pi-sort-amount-down"
      default:
        return "pi-sort-alt"
    }
  }

  /**
   * @param {false | import("@tanstack/react-table").SortDirection} sortDir
   */
  const renderSortIcon = (sortDir) => {
    if (!canSort) {
      return null
    }

    return (
      <i className={`table__sort-icon pi ${getSortIconClassName(sortDir)}`} />
    )
  }

  function renderFilterElement() {
    if (!columnMeta?.filterElement) {
      return null
    }

    return columnMeta.filterElement({
      value: filterDraftValue,
      filterCallback: setFilterDraftValue,
    })
  }

  function renderFilter() {
    if (!canFilter) {
      return null
    }

    return (
      <>
        <TableFilterButton
          isFiltered={isFiltered}
          onClick={handleFilterButtonClick}
          disabled={!hasData && !hasActiveFilters}
        />
        <TableFilterOverlay
          ref={overlayRef}
          renderFilterElement={renderFilterElement}
          onApply={handleFilterApply}
          onClear={handleFilterClear}
        />
      </>
    )
  }

  /**
   * @param {React.MouseEvent<HTMLButtonElement>} e
   */
  function handleFilterButtonClick(e) {
    e.stopPropagation()

    if (
      openOverlayRef?.current &&
      openOverlayRef.current !== overlayRef.current
    ) {
      openOverlayRef.current.hide()
    }

    overlayRef.current?.toggle(e)
    openOverlayRef.current = overlayRef.current
  }

  function handleFilterApply() {
    header.column.setFilterValue(filterDraftValue)
    overlayRef.current?.hide()
  }

  function handleFilterClear() {
    setFilterDraftValue(undefined)
    header.column.setFilterValue(undefined)
    overlayRef.current?.hide()
  }

  return (
    <th
      className={className}
      onClick={
        isSortEnabled ? header.column.getToggleSortingHandler() : undefined
      }
      style={{ ...pinnedStyle }}
    >
      {header.isPlaceholder ? null : (
        <div className="table__cell-content">
          {flexRender(header.column.columnDef.header, header.getContext())}

          {renderSortIcon(sortDir)}

          {renderFilter()}
        </div>
      )}
    </th>
  )
}
